import type { OperationButton } from "@/components/proTable/interface";
import { loginService } from "@/api/loginService";
import CryptoJS from "crypto-js";
import { PAYMENT_TYPE } from "../../../../common/enum/purchasePayment";
import { formatCurrency } from "../../../utils";
import type { CustomerVo, Product } from "@zhihaoscm/entity";

import { useUserStore } from "@/stores/user";
import { useAccountStore } from "@/stores/account";
import { DEFAULT_VALUE } from "@/config";
import { areaLib } from "@/utils/areaLib";
/** 获取细度模数(最小细模数-最大细度模数) */
export const getFm = (product?: Product) => {
    if (!product) {
        return;
    }
    return [product.minFm, product.maxFm].filter(Boolean).join("-") || DEFAULT_VALUE;
};

/** 获取行内值, 前缀+值+后缀 */
export const getInlineValue = (prefix?: string, value?: string | number, suffix?: string) => {
    if (value === undefined || value === "" || value === null) {
        return "";
    } else {
        return [prefix, value, suffix].filter(Boolean).join("");
    }
};

/** 获取行内价格 */
export const getInlinePrice = (value?: string | number, suffix?: string, prefix: string = "¥") => {
    return getInlineValue(prefix, formatCurrency(value).replace(DEFAULT_VALUE, ""), suffix);
};

/** 获取行内单价 */
export const getInlineUnitPrice = (value?: string | number, suffix?: string) => {
    return getInlinePrice(value, suffix ?? "/吨");
};

/** 获取行内百分数 */
export const getInlinePercent = (value?: string | number) => {
    return getInlineValue(undefined, value || DEFAULT_VALUE, "%");
};

// 格式化银行账户号
export const getBankCard = (cardNum?: number | string) => {
    if (!cardNum) {
        return;
    }
    const cardStr = cardNum.toString();
    return cardStr.replace(/(\d{4})(?=\d)/g, "$1 ");
};

/** 为页面操作配置添加返回 */
export const appendBackButton = (buttons: OperationButton[], handleBack: (entity?: any) => void) => {
    buttons.push({
        label: "返回",
        click: handleBack
    });
    return buttons;
};

/** 获取船名 */
export const getShipName = (entity: { name?: string; cnName?: string; cnname?: string }, separator = "/") => {
    return [entity.cnName || entity.cnname, entity.name].filter(Boolean).join(separator);
};

/** 获取商品的地址 */
export const getProductAddress = (product?: Product) => {
    if (!product || !product.address) return "--";
    const fullName = areaLib.getFullAreaName(product.provinceCode, product.cityCode, product.regionCode);
    return fullName + product.address;
};

/** 判断页面是否滚动到底部 */
let _scrollTop = 0;
export const isInTheEnd = () => {
    // 滚动条距离顶部的距离
    const scrollTop = document.documentElement.scrollTop;
    _scrollTop = scrollTop;
    // 文档的总高度
    const documentHeight = document.documentElement.scrollHeight;
    // 浏览器窗口的可见高度
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    return scrollTop + windowHeight >= documentHeight - 10;
};

/** 数据加载完成之后滚动条在当前位置 */
export const setScrollBarInNow = () => {
    window.scrollTo(0, _scrollTop);
};

export const paymentPageName = (userStore: any, paymentType?: number) => {
    if (userStore.isCustomer) {
        return "付款";
    } else if (userStore.isSupplier) {
        return "收款";
    } else {
        if (PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.match(paymentType)) {
            return "收款";
        } else if (PAYMENT_TYPE.MIDSTREAM_UPSTREAM.match(paymentType)) {
            return "付款";
        }
        return "收付款";
    }
};

/**获取用户信息 */
export const getUserInfo = async () => {
    const data = await loginService.getMe();
    const { customerService } = await import("@/api/customerService");

    data.actualAccount.realName = await customerService.getCustomerRealName(data.actualAccount.id);
    data.actualAccount.mobile = await customerService.getCustomerRealMobile(data.actualAccount.id);
    const customerVo = {
        customer: data.actualAccount,
        permissions: data.permissions,
        memberPermission: data.actualMemberPermission,
        proxyAccount: data.proxyAccount,
        proxyMemberPermission: data.proxyMemberPermission
    } as unknown as CustomerVo;
    const userStore = useUserStore();
    userStore.setUserInfo(customerVo as unknown as CustomerVo);
    userStore.setBindTime(data.bindTime);
    userStore.setNickName(data.nickName);
    userStore.setAuthorizationState(data.authorizationState);
    const accountStore = useAccountStore();
    accountStore.setAccountType();
    accountStore.setActualAccount(customerVo.customer);
    accountStore.setActualMemberPermission(customerVo.memberPermission);
    accountStore.setProxyAccount((customerVo as any).proxyAccount);
    accountStore.setProxyMemberPermission((customerVo as any).proxyMemberPermission);
    return;
};

/**解密数据 */
export const decryptData = (data: string) => {
    const keyHex = CryptoJS.enc.Utf8.parse("1234567891234567"); // 秘钥
    return JSON.parse(
        CryptoJS.AES.decrypt(data, keyHex, {
            mode: CryptoJS.mode.ECB,
            padding: CryptoJS.pad.Pkcs7
        }).toString(CryptoJS.enc.Utf8)
    );
};

/**校验是否有会员权限 */
export const validateMemberPermission = (permission: string) => {
    const { memberInfo } = useUserStore();
    const accountStore = useAccountStore();
    const memberPermission = accountStore.proxyMemberPermission
        ? accountStore.proxyMemberPermission
        : memberInfo.memberPermission;
    return memberPermission && !!(memberPermission as any)[permission];
};

/** 获取子账号权限 */
export const getPermission = (code: number) => {
    const userStore = useUserStore();
    const accountStore = useAccountStore();
    const userInfo = userStore.userInfo;
    if (accountStore.isSubAccount) {
        return !!userInfo.permissions && userInfo.permissions?.includes(code);
    }
    return true;
};

// 公众页路径
export const getConsoleAdminUrl = (path: string) => {
    // const consoleUIBaseUrl = `${window.location.origin}${useRuntimeConfig().public.publicPath}`;

    if (window.location.protocol === "http:") {
        // 本地环境
        return `http://localhost:5173${path}`;
    } else {
        // 线上
        return `${window.location.origin}${path}`;
    }
    // const consoleAdminBaseUrl = consoleUIBaseUrl.endsWith("/console")
    //     ? consoleUIBaseUrl.slice(0, -8)
    //     : consoleUIBaseUrl;

    // return `${consoleAdminBaseUrl}${path}`;
};
/** 跳转公众页 */
export const openConsoleAdminPage = (path: string, _blank = false) => {
    const consoleAdminUrl = getConsoleAdminUrl(path);
    // 新窗口打开
    window.open(consoleAdminUrl, _blank ? "_blank" : "_self");
};

// 更新页签 icon
export function updateFavicon(url: string): void {
    // 查找 link 标签
    let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");

    if (!link) {
        // 如果不存在，创建新的 link 标签
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }

    // 更新 favicon 链接
    link.href = url;
}
